import React from 'react';
import { X, Package, Shirt, User, Calendar, MapPin, Building, Home, FileText, Download, Droplets } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const DetalleSolicitudModal = ({ isOpen, onClose, solicitud, tipo = 'insumos' }) => {
  if (!isOpen || !solicitud) return null;

  const isInsumos = tipo === 'insumos';
  const Icon = isInsumos ? Package : Shirt;
  const colorClass = isInsumos ? 'blue' : 'purple';

  const estadoColors = {
    'Pendiente': 'bg-yellow-100 text-yellow-800',
    'En camino': 'bg-blue-100 text-blue-800',
    'Entregado': 'bg-green-100 text-green-800',
    'Cancelado': 'bg-red-100 text-red-800'
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const title = `Detalle de Solicitud - ${isInsumos ? 'Insumos' : 'Lencería'}`;
    const dateStr = new Date(solicitud.createdAt).toLocaleString('es-ES', {
      dateStyle: 'long',
      timeStyle: 'short'
    });

    // Header
    doc.setFontSize(18);
    doc.text(title, 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generado el: ${new Date().toLocaleString()}`, 14, 28);
    if (solicitud._id) {
        doc.text(`ID: ${solicitud._id}`, 14, 33);
    }
    
    doc.setTextColor(0);
    doc.setFontSize(12);
    doc.text(`Fecha Solicitud: ${dateStr}`, 14, 45);
    doc.text(`Estado: ${solicitud.estado}`, 14, 52);
    doc.text(`Solicitante: ${solicitud.solicitante?.nombre || 'N/A'}`, 14, 59);

    let yPos = 70;

    // Info Portafolio
    doc.setFontSize(14);
    doc.text("Información del Sitio", 14, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.text(`Portafolio: ${solicitud.edificio?.nombre || 'N/A'}`, 14, yPos);
    doc.text(`Ciudad: ${solicitud.edificio?.ciudad || 'N/A'}`, 14, yPos + 6);
    if (solicitud.edificio?.direccion) {
      doc.text(`Dirección: ${solicitud.edificio.direccion}`, 14, yPos + 12);
    }
    
    // Info Hospedaje
    const hospedajeName = solicitud.hospedaje?.nombre || solicitud.nombrePropiedad;
    if (hospedajeName) {
        doc.text(`Hospedaje: ${hospedajeName}`, 100, yPos);
    }
    
    yPos += 25;

    // Observaciones
    if (solicitud.observaciones) {
        doc.setFontSize(12);
        doc.text("Observaciones:", 14, yPos);
        doc.setFontSize(10);
        const splitObservaciones = doc.splitTextToSize(solicitud.observaciones, 180);
        doc.text(splitObservaciones, 14, yPos + 7);
        yPos += (splitObservaciones.length * 5) + 15;
    }

    // Totales
    doc.setFontSize(12);
    const totalText = `Total Solicitado: ${solicitud.total_solicitado ?? solicitud.total_items} unidades | Tipos de Items: ${solicitud.items?.length || 0}`;
    doc.text(totalText, 14, yPos);
    yPos += 10;

    // Tabla
    const tableColumn = ["Nombre", "Tipo", "Cantidad", ...(isInsumos ? ["Categoría"] : ["Peso Unit.", "Peso Total"]), "Observación"];
    const tableRows = solicitud.items?.map(item => {
      const isItemInsumo = item.tipo_item === 'insumo';
      const row = [
        item.nombre || 'N/A',
        isItemInsumo ? 'Insumo' : 'Prenda',
        item.cantidad,
      ];
      
      if (isInsumos) {
          row.push(item.categoria || 'N/A');
      } else {
          row.push(isItemInsumo ? '-' : (item.peso ? `${item.peso} g` : 'N/A'));
          row.push(isItemInsumo ? '-' : ((item.peso && item.cantidad) ? `${(item.peso * item.cantidad)} g` : 'N/A'));
      }
      
      row.push(item.observacion || '');
      return row;
    }) || [];

    autoTable(doc, {
        startY: yPos,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        headStyles: { fillColor: isInsumos ? [59, 130, 246] : [147, 51, 234] },
        styles: { fontSize: 9 },
    });

    doc.save(`solicitud_${isInsumos ? 'insumos' : 'lenceria'}_${solicitud._id || Date.now()}.pdf`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 bg-${colorClass}-50 border-b border-${colorClass}-200 flex items-center justify-between`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 bg-${colorClass}-100 rounded-lg`}>
              <Icon className={`h-6 w-6 text-${colorClass}-600`} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Detalle de Solicitud - {isInsumos ? 'Insumos' : 'Lencería'}
              </h2>
              <p className="text-sm text-gray-600">
                {new Date(solicitud.createdAt).toLocaleString('es-ES', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Información General */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Columna Izquierda */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Información del Portafolio
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <Building className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Portafolio</p>
                      <p className="font-medium text-gray-900">
                        {solicitud.edificio?.nombre || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Ciudad</p>
                      <p className="text-gray-900">
                        {solicitud.edificio?.ciudad || 'N/A'}
                      </p>
                    </div>
                  </div>
                  {solicitud.edificio?.direccion && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Dirección</p>
                        <p className="text-gray-900 text-sm">
                          {solicitud.edificio.direccion}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Propiedad/Hospedaje */}
              {(solicitud.hospedaje || solicitud.nombrePropiedad) && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Activo
                  </h3>
                  <div className={`bg-${colorClass}-50 rounded-lg p-4`}>
                    <div className="flex items-start gap-2">
                      <Home className={`h-4 w-4 text-${colorClass}-600 mt-0.5`} />
                      <div>
                        <p className="text-xs text-gray-500">Nombre</p>
                        <p className={`font-medium text-${colorClass}-900`}>
                          {solicitud.hospedaje?.nombre || solicitud.nombrePropiedad || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* observaciones */}
             
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Observaciones
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900">{solicitud.observaciones || 'Sin observaciones'}</p>
                  </div>
                </div>
              
            </div>

            {/* Columna Derecha */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Solicitante
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Nombre</p>
                      <p className="font-medium text-gray-900">
                        {solicitud.creator?.nombre || solicitud.solicitante?.nombre || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Estado y Totales
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Estado</p>
                    <Badge className={estadoColors[solicitud.estado] || 'bg-gray-100 text-gray-800'}>
                      {solicitud.estado}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Solicitado</p>
                    <p className={`text-2xl font-bold text-${colorClass}-600`}>
                      {solicitud.total_solicitado ?? solicitud.total_items} unidades
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tipos de Items</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {solicitud.items?.length || 0} tipo(s)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Observaciones */}
          {solicitud.observaciones && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Observaciones
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm whitespace-pre-wrap">
                  {solicitud.observaciones}
                </p>
              </div>
            </div>
          )}

          {/* Tabla de Items */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Icon className="h-4 w-4" />
              Items Solicitados
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Nombre</TableHead>
                    <TableHead className="font-semibold text-center">Tipo</TableHead>
                    <TableHead className="font-semibold text-center">Cantidad</TableHead>
                    {isInsumos && (
                      <TableHead className="font-semibold">Categoría</TableHead>
                    )}
                    {!isInsumos && (
                      <>
                        <TableHead className="font-semibold text-center">Peso Unit.</TableHead>
                        <TableHead className="font-semibold text-center">Peso Total</TableHead>
                      </>
                    )}
                    {solicitud.items?.[0]?.observacion && (
                      <TableHead className="font-semibold">Observación</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {solicitud.items && solicitud.items.length > 0 ? (
                    solicitud.items.map((item, index) => {
                      const isItemInsumo = item.tipo_item === 'insumo';
                      return (
                        <TableRow key={index} className={isItemInsumo ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-gray-50'}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {isItemInsumo 
                                ? <Droplets className="h-4 w-4 text-amber-600" />
                                : <Icon className={`h-4 w-4 text-${colorClass}-600`} />
                              }
                              <span className="font-medium">{item.nombre || 'N/A'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {isItemInsumo ? (
                              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 border border-amber-300">
                                Insumo
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                Prenda
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className={isItemInsumo ? 'bg-amber-50 text-amber-700 border-amber-300' : `bg-${colorClass}-50 text-${colorClass}-700`}>
                              {item.cantidad}
                            </Badge>
                          </TableCell>
                          {isInsumos && (
                            <TableCell>
                              <Badge variant="outline" className="bg-gray-100 text-gray-700">
                                {item.categoria || 'N/A'}
                              </Badge>
                            </TableCell>
                          )}
                          {!isInsumos && (
                            <>
                              <TableCell className="text-center text-sm text-gray-600">
                                {isItemInsumo ? '-' : (item.peso ? `${item.peso} g` : 'N/A')}
                              </TableCell>
                              <TableCell className="text-center">
                                {isItemInsumo ? (
                                  <span className="text-gray-400">-</span>
                                ) : (
                                  <span className={`font-semibold text-${colorClass}-600`}>
                                    {item.peso && item.cantidad 
                                      ? `${(item.peso * item.cantidad)} g`
                                      : 'N/A'}
                                  </span>
                                )}
                              </TableCell>
                            </>
                          )}
                          {item.observacion && (
                            <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                              {item.observacion}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isInsumos ? 4 : 6} className="text-center py-8 text-gray-500">
                        No hay items en esta solicitud
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={handleExportPDF}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Exportar PDF
          </Button>
          <Button onClick={onClose} variant="outline">
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DetalleSolicitudModal;
